// screen: Register (self-serve sign-up, change add-viewstream-v3 §17) — name, e-mail, password, organisation, terms.
// A sign-up started from the WordPress plugin / Next.js kit carries the /connect query (app, site, redirect_uri, state,
// code_challenge…); it goes to the API with the form and comes back after the e-mail verification as `continue`.
import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useLingui, Trans } from '@lingui/react/macro';
import { ApiError } from '@viewstream/api-client';
import { Button, Checkbox, EmptyState, Field, Input, Skeleton } from '@viewstream/ui';
import { useLocale } from '~/app/locale';
import { ProductName, LocaleToggle } from '~/app/brand';
import { postSignup, useSignupConfig } from '~/app/selfserve';

const str = (v: unknown) => (typeof v === 'string' ? v : undefined);

export const Route = createFileRoute('/register')({
  validateSearch: (s: Record<string, unknown>) => ({
    app: str(s['app']), site: str(s['site']), redirect_uri: str(s['redirect_uri']), state: str(s['state']),
    code_challenge: str(s['code_challenge']), code_challenge_method: str(s['code_challenge_method']), lang: str(s['lang']),
  }),
  component: Register,
});

declare global {
  interface Window { turnstile?: { render: (el: HTMLElement, o: Record<string, unknown>) => string; reset: (id?: string) => void } }
}

/** Cloudflare Turnstile widget (only when the API names a site key). */
function Turnstile({ siteKey, lang, onToken }: { siteKey: string; lang: string; onToken: (t: string) => void }) {
  const ref = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    let cancelled = false;
    const render = () => {
      if (cancelled || !ref.current || !window.turnstile) return;
      ref.current.innerHTML = '';
      window.turnstile.render(ref.current, { sitekey: siteKey, language: lang, callback: onToken, 'expired-callback': () => onToken('') });
    };
    if (window.turnstile) render();
    else {
      const s = document.createElement('script');
      s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      s.async = true; s.onload = render;
      document.head.appendChild(s);
    }
    return () => { cancelled = true; };
  }, [siteKey, lang, onToken]);
  return <div ref={ref} className="my-3 min-h-16" />;
}

function hostOf(site?: string): string {
  try { return site ? new URL(site).host : ''; } catch { return ''; }
}

function Register() {
  const { t } = useLingui();
  const { locale, setLocale } = useLocale();
  const search = Route.useSearch();
  const cfg = useSignupConfig();
  const [f, setF] = React.useState({ name: '', email: '', password: '', organisation: '', website: '' });
  const [accept, setAccept] = React.useState(false);
  const [token, setToken] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState<Record<string, string>>({});
  const [sent, setSent] = React.useState<string | null>(null);
  React.useEffect(() => { if (search.lang === 'he' || search.lang === 'en') setLocale(search.lang); }, [search.lang, setLocale]);
  React.useEffect(() => { document.title = `${t`Create your free account`} · ViewStream Studio`; }, [t]);
  const site = hostOf(search.site);
  const app = search.app === 'wordpress' ? 'WordPress' : search.app === 'nextjs' ? 'Next.js' : '';

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!cfg.data) return;
    setBusy(true); setErr({});
    try {
      await postSignup({
        ...f, lang: locale === 'en' ? 'en' : 'he', accept_terms: accept, terms_version: cfg.data.terms_version, privacy_version: cfg.data.privacy_version,
        turnstile_token: token || undefined,
        ...(search.app ? { app: search.app, site: search.site, redirect_uri: search.redirect_uri, state: search.state, code_challenge: search.code_challenge, code_challenge_method: search.code_challenge_method || 'S256' } : {}),
      });
      setSent(f.email);
    } catch (ex) {
      if (ex instanceof ApiError && ex.status === 422) {
        const m: Record<string, string> = {};
        for (const x of ex.problem?.errors ?? []) m[x.field] = x.detail;
        if (!Object.keys(m).length) m['form'] = ex.problem?.detail ?? t`Check the form.`;
        setErr(m);
        if (window.turnstile) window.turnstile.reset();
      } else if (ex instanceof ApiError && ex.status === 429) {
        setErr({ form: t`Too many sign-ups from this network. Try again in an hour.` });
      } else setErr({ form: ex instanceof Error ? ex.message : String(ex) });
    } finally { setBusy(false); }
  }

  const shell = (body: React.ReactNode) => (
    <main className="flex min-h-dvh items-center justify-center p-6">
      <div className="w-full max-w-md rounded-xl border border-border bg-surface p-8 shadow-card">
        <div className="mb-6 flex items-center justify-between"><ProductName large /><LocaleToggle /></div>
        {body}
      </div>
    </main>
  );
  if (cfg.isPending) return shell(<Skeleton className="h-64 w-full" />);
  if (!cfg.data) return shell(<EmptyState title={t`Sign-up is not open yet`} description={t`Write to sales@viewstream.co.il and we will open an account for you.`} />);
  if (sent) return shell(<EmptyState title={t`Check your email`} description={t`We sent a confirmation link to ${sent}. Open it within 48 hours to activate your free account.`} />);
  const fr = cfg.data.free;
  return shell(
    <form onSubmit={submit} noValidate>
      <h1 className="mb-1 text-xl font-semibold">{t`Create your free ViewStream account`}</h1>
      {app && site ? (
        <p className="mb-3 text-sm text-fg-muted" data-testid="register-app">{t`Create your free account to connect ${site} (${app}). You will come back to approve the connection.`}</p>
      ) : null}
      <p className="mb-4 text-sm text-fg-muted">{t`Free forever: ${fr.storage_gb} GB storage, ${fr.transcode_minutes} encoding minutes and ${fr.delivered_gb} GB of viewing a month, files up to ${fr.max_upload_gb} GB, AI summaries. No credit card.`}</p>
      <Field id="name" label={t`Your name`} error={err['name']}><Input autoComplete="name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required /></Field>
      <Field id="email" label={t`Work email`} error={err['email']}><Input type="email" autoComplete="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} dir="ltr" className="ltr-isolate" required /></Field>
      <Field id="password" label={t`Password`} hint={t`At least ${cfg.data.min_password} characters.`} error={err['password']}><Input type="password" autoComplete="new-password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} dir="ltr" required /></Field>
      <Field id="organisation" label={t`Organisation or channel name`} error={err['organisation']}><Input autoComplete="organization" value={f.organisation} onChange={(e) => setF({ ...f, organisation: e.target.value })} required /></Field>
      {/* honeypot: hidden from people, filled by bots */}
      <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden className="hidden" value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} />
      <div className="my-3 flex items-start gap-2 text-sm">
        <Checkbox id="accept-terms" checked={accept} onCheckedChange={(v) => setAccept(v === true)} aria-describedby="terms-err" />
        <label htmlFor="accept-terms">
          <Trans>I accept the <a className="text-primary underline" href={cfg.data.terms_url} target="_blank" rel="noopener">terms of service</a> and the <a className="text-primary underline" href={cfg.data.privacy_url} target="_blank" rel="noopener">privacy policy</a>.</Trans>
        </label>
      </div>
      {err['accept_terms'] || err['terms_version'] ? <p id="terms-err" className="text-sm text-danger">{err['accept_terms'] ?? err['terms_version']}</p> : null}
      {cfg.data.turnstile_site_key ? <Turnstile siteKey={cfg.data.turnstile_site_key} lang={locale} onToken={setToken} /> : null}
      {err['turnstile_token'] ? <p className="text-sm text-danger">{t`The anti-robot check failed — try again.`}</p> : null}
      {err['form'] || err['app'] || err['site'] || err['redirect_uri'] || err['code_challenge'] ? <p className="mb-2 text-sm text-danger" role="alert">{err['form'] ?? err['app'] ?? err['site'] ?? err['redirect_uri'] ?? err['code_challenge']}</p> : null}
      <Button type="submit" className="w-full" loading={busy} disabled={!accept || (!!cfg.data.turnstile_site_key && !token)}>{t`Create free account`}</Button>
      <p className="mt-4 text-center text-sm"><Trans>Already have an account? <a className="text-primary underline-offset-4 hover:underline" href="/login">Sign in</a></Trans></p>
    </form>,
  );
}
